import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import SITE_CONFIG from '../config/site.config';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const contactMethods = [
    {
      icon: 'flaticon-phone',
      title: 'Phone',
      value: SITE_CONFIG.phoneDisplay,
      description: 'Call us during business hours'
    },
    {
      icon: 'fas fa-building',
      title: 'Office',
      value: SITE_CONFIG.officePhone,
      description: 'Reach our head office desk'
    },
    {
      icon: 'flaticon-email',
      title: 'Email',
      value: SITE_CONFIG.email,
      description: 'We respond within 24 hours'
    },
    {
      icon: 'flaticon-location',
      title: 'Head Office',
      value: SITE_CONFIG.addressLines.join('\n'),
      description: 'SSVASS Enterprises, Patna'
    },
    {
      icon: 'fas fa-globe',
      title: 'Website',
      value: SITE_CONFIG.websiteDisplay,
      description: 'Visit our official website'
    },
    {
      icon: 'flaticon-clock',
      title: 'Working Hours',
      value: `${SITE_CONFIG.workingDays}\n${SITE_CONFIG.workingHours}`,
      description: 'Office support hours'
    }
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      url: SITE_CONFIG.facebook,
      icon: 'fab fa-facebook-f'
    },
    {
      name: 'Twitter',
      url: SITE_CONFIG.twitter,
      icon: 'fab fa-twitter'
    },
    {
      name: 'Instagram',
      url: SITE_CONFIG.instagram,
      icon: 'fab fa-instagram'
    },
    {
      name: 'LinkedIn',
      url: SITE_CONFIG.linkedin,
      icon: 'fab fa-linkedin-in'
    },
    {
      name: 'YouTube',
      url: SITE_CONFIG.youtube,
      icon: 'fab fa-youtube'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      await axios.post(`${API_CONFIG.BASE_URL}/contact`, formData, {
        headers: API_CONFIG.HEADERS
      });

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      setErrors({ submit: 'Failed to send message. Please try again later.' });
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Page Header Start */}
      <div className="page-header">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2>Contact Us</h2>
            </div>
            <div className="col-12">
              <a href="/">Home</a>
              <a href="/contact">Contact Us</a>
            </div>
          </div>
        </div>
      </div>
      {/* Page Header End */}

      {/* Contact Methods Section */}
      <div className="contact-methods">
        <div className="container">
          <div className="section-header text-center">
            <p>Get in Touch</p>
            <h2>We're Here to Help</h2>
          </div>
          <div className="methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="method-card">
                <div className="method-icon">
                  <i className={method.icon}></i>
                </div>
                <h3>{method.title}</h3>
                <p className="method-value">{method.value}</p>
                <p className="method-description">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="contact-content">
        <div className="container">
          <div className="row">
            {/* Contact Form */}
            <div className="col-lg-8">
              <div className="contact-form-container">
                <div className="form-header">
                  <h2>Send us a Message</h2>
                  <p>Have a question or want to work together? We'd love to hear from you.</p>
                </div>

                {submitted && (
                  <div className="success-message">
                    <i className="flaticon-check"></i>
                    <h3>Thank You!</h3>
                    <p>Your message has been sent successfully. We will get back to you soon.</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  {errors.submit && <div className="error-message">{errors.submit}</div>}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && <span className="error-text">{errors.name}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-text">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXXXXXXX"
                        className={errors.phone ? 'error' : ''}
                      />
                      {errors.phone && <span className="error-text">{errors.phone}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject *</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        className={errors.subject ? 'error' : ''}
                      />
                      {errors.subject && <span className="error-text">{errors.subject}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your inquiry..."
                      rows="6"
                      className={errors.message ? 'error' : ''}
                    ></textarea>
                    {errors.message && <span className="error-text">{errors.message}</span>}
                  </div>

                  <p className="form-disclaimer">
                    By submitting this form, you agree to receive communications from us via SMS,
                    email, Whatsapp, RCS or phone.{' '}
                    <Link to="/terms">Terms &amp; Conditions</Link>
                  </p>

                  <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              {/* Map */}
              <div className="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3597.67333771497!2d85.15542717504073!3d25.615768877443823!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ed58f0fd513ca9%3A0x807aa8506ef163bb!2sBari%20Path%2C%20Patna%2C%20Bihar!5e0!3m2!1sen!2sin!4v1753131145337!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSVASS Location"
                ></iframe>
              </div>

              {/* Info Card */}
              <div className="info-section">
                <div className="info-card">
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <i className="fas fa-building"></i>
                    <div>
                      <p className="info-label">Company</p>
                      <p className="info-value">{SITE_CONFIG.name}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="flaticon-location"></i>
                    <div>
                      <p className="info-label">Head Office</p>
                      <p className="info-value">{SITE_CONFIG.addressLines.join('\n')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="flaticon-phone"></i>
                    <div>
                      <p className="info-label">Phone</p>
                      <p className="info-value">
                        <a href={`tel:${SITE_CONFIG.phoneDisplay.replace(/\s/g, '')}`}>{SITE_CONFIG.phoneDisplay}</a>
                      </p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-phone-square-alt"></i>
                    <div>
                      <p className="info-label">Office</p>
                      <p className="info-value">
                        <a href={`tel:${SITE_CONFIG.officePhone}`}>{SITE_CONFIG.officePhone}</a>
                      </p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="flaticon-email"></i>
                    <div>
                      <p className="info-label">Email</p>
                      <p className="info-value">
                        <a href={`mailto:${SITE_CONFIG.email}`}>{SITE_CONFIG.email}</a>
                      </p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-globe"></i>
                    <div>
                      <p className="info-label">Website</p>
                      <p className="info-value">
                        <a href={SITE_CONFIG.websiteUrl} target="_blank" rel="noopener noreferrer">
                          {SITE_CONFIG.websiteDisplay}
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="flaticon-clock"></i>
                    <div>
                      <p className="info-label">Working Hours</p>
                      <p className="info-value">{SITE_CONFIG.workingDays}{'\n'}{SITE_CONFIG.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Follow Us */}
              <div className="follow-us">
                <h3>Follow Us</h3>
                <p>Stay connected with SSVASS exhibition updates, announcements, and highlights.</p>
                <div className="social-links">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      className="social-link"
                      title={social.name}
                      aria-label={`Follow SSVASS on ${social.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className={social.icon}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="contact-faq">
        <div className="container">
          <div className="section-header text-center">
            <p>Common Questions</p>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is SSVASS Exhibition?</h3>
              <p>SSVASS National Gems & Jewellery Exhibition is India's leading platform for showcasing jewellery and gemstone products to thousands of buyers and enthusiasts.</p>
            </div>
            <div className="faq-item">
              <h3>How can I register as a visitor?</h3>
              <p>You can register for free as a visitor through our website registration page. Simply fill in your details and you'll receive confirmation via email.</p>
            </div>
            <div className="faq-item">
              <h3>Can I book a stall for my business?</h3>
              <p>Yes! We offer various stall packages for exhibitors. Visit our Stall Booking page to explore available options and pricing.</p>
            </div>
            <div className="faq-item">
              <h3>What are the exhibition dates?</h3>
              <p>The 21st edition of SSVASS will be held from 30 Aug to 01 Sep 2026 at Gyan Bhawan, Gandhi Maidan, Patna.</p>
            </div>
            <div className="faq-item">
              <h3>Is there any parking facility?</h3>
              <p>Yes, ample parking is available at the venue for visitors and exhibitors. Additional valet parking services are also available.</p>
            </div>
            <div className="faq-item">
              <h3>How do I contact the organizers?</h3>
              <p>You can reach us via phone, email, or the contact form on this page. Our team responds within 24 hours to all inquiries.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
